import React, { useState, useEffect, useRef } from "react";
import * as XLSX from "xlsx";

interface FilePreviewModalProps {
  isOpen: boolean;
  fileUrl: string;
  fileName?: string;
  onClose: () => void;
}

const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  isOpen,
  fileUrl,
  fileName = "File",
  onClose,
}) => {
  const [fileType, setFileType] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [closeHover, setCloseHover] = useState(false);
  const [downloadHover, setDownloadHover] = useState(false);
  const [csvData, setCsvData] = useState<any[][]>([]);
  const [excelData, setExcelData] = useState<any>(null);
  const [corsError, setCorsError] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string>("");
  const spreadsheetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (fileUrl && isOpen) {
      const type = getFileType(fileName, fileUrl);
      setFileType(type);
      setLoading(true);

      if (type === "pdf") {
        fetchAndConvertToBlob(fileUrl, type);
      } else if (type === "image") {
        setLoading(false);
      } else {
        setLoading(false);
      }
    }

    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [fileUrl, isOpen, fileName]);

  const fetchAndConvertToBlob = async (fileUrl: string, type: string) => {
    try {
      setLoading(true);
      setCorsError(false);

      const response = await fetch(fileUrl, {
        method: "GET",
        headers: {
          "Content-Disposition": "inline; filename=" + fileName,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      // Create blob URL with proper MIME type
      const mimeType = type === "pdf" ? "application/pdf" : "application/octet-stream";
      const blobWithType = new Blob([blob], { type: mimeType });
      const url = URL.createObjectURL(blobWithType);
      setBlobUrl(url);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching file:", error);

      if (error instanceof TypeError) {
        setCorsError(true);
      }

      setFileType("error");
      setLoading(false);
    }
  };

  const getFileType = (fileName: string, url: string): string => {
    const nameToCheck = fileName || url;
    const extension = nameToCheck.split(".").pop()?.toLowerCase() || "";

    if (["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp"].includes(extension)) {
      return "image";
    }
    if (extension === "pdf") {
      return "pdf";
    }
    if (extension === "csv") {
      return "csv";
    }
    if (["xlsx", "xls"].includes(extension)) {
      return "excel";
    }
    if (["doc", "docx"].includes(extension)) {
      return "word";
    }
    if (["mp4", "webm", "ogg", "mov"].includes(extension)) {
      return "video";
    }
    if (["mp3", "wav", "ogg"].includes(extension)) {
      return "audio";
    }

    return "unknown";
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenInNewTab = () => {
      window.open(fileUrl, "_blank");
  };

  if (!isOpen) return null;

  const renderPreview = () => {
    if (loading) {
      return (
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Loading preview...</p>
        </div>
      );
    }

    switch (fileType) {
      case "image":
        return (
          <div style={styles.imageContainer}>
            <img
              src={fileUrl}
              alt={fileName}
              style={styles.image}
              onError={() => setFileType("error")}
            />
          </div>
        );

      case "pdf":
        return (
          <div style={styles.pdfContainer}>
            <iframe
              src={blobUrl || `${fileUrl}#view=FitH&toolbar=0&navpanes=0`}
              style={styles.iframe}
              title={fileName}
              onError={() => setFileType("error")}
            />
          </div>
        );

      case "csv":
        return (
          <iframe
            src={fileUrl}
            style={styles.iframe}
            title={fileName}
            onError={() => setFileType("error")}
          />
        );

      case "excel":
        return (
          <iframe
            src={fileUrl}
            style={styles.iframe}
            title={fileName}
            onError={() => setFileType("error")}
          />
        );

      case "word":
        return (
          <div style={styles.unsupportedContainer}>
            <div style={styles.wordIcon}>📝</div>
            <h3 style={styles.unsupportedTitle}>Word Document</h3>
            <p style={styles.unsupportedText}>
              Word documents require downloading to view. Click the button below to download and
              open in Word or Google Docs.
            </p>
            <button
              style={{
                ...styles.downloadButton,
                ...(downloadHover ? styles.downloadButtonHover : {}),
              }}
              onClick={handleDownload}
              onMouseEnter={() => setDownloadHover(true)}
              onMouseLeave={() => setDownloadHover(false)}
            >
              Download Word Document
            </button>
          </div>
        );

      case "video":
        return (
          <div style={styles.videoContainer}>
            <video src={fileUrl} controls style={styles.video} onError={() => setFileType("error")}>
              Your browser does not support the video tag.
            </video>
          </div>
        );

      case "audio":
        return (
          <div style={styles.audioContainer}>
            <audio src={fileUrl} controls style={styles.audio} onError={() => setFileType("error")}>
              Your browser does not support the audio tag.
            </audio>
          </div>
        );

      case "error":
        return (
          <div style={styles.errorContainer}>
            <div style={styles.errorIcon}>⚠️</div>
            <h3 style={styles.errorTitle}>
              {corsError ? "CORS Error - Cannot Load File" : "Failed to Load File"}
            </h3>
            <p style={styles.errorText}>
              {corsError ? (
                <>
                  The file cannot be previewed due to security restrictions (CORS policy). The
                  storage server needs to be configured to allow cross-origin requests.
                  <br />
                  <br />
                  <strong>Quick fixes:</strong>
                  <br />
                  • Download the file directly
                  <br />
                  • Open the file in a new tab
                  <br />• Configure CORS headers on your storage bucket
                </>
              ) : (
                "The file could not be loaded. Please try downloading it instead."
              )}
            </p>
            <div style={styles.buttonGroup}>
              <button
                style={{
                  ...styles.downloadButton,
                  ...(downloadHover ? styles.downloadButtonHover : {}),
                }}
                onClick={handleDownload}
                onMouseEnter={() => setDownloadHover(true)}
                onMouseLeave={() => setDownloadHover(false)}
              >
                Download File
              </button>
              <button
                style={{
                  ...styles.secondaryButton,
                }}
                onClick={handleOpenInNewTab}
              >
                Open in New Tab
              </button>
            </div>
          </div>
        );

      default:
        return (
          <div style={styles.unsupportedContainer}>
            <div style={styles.unsupportedIcon}>📎</div>
            <h3 style={styles.unsupportedTitle}>Unknown File Type</h3>
            <p style={styles.unsupportedText}>Unable to determine file type for preview.</p>
            <button
              style={{
                ...styles.downloadButton,
                ...(downloadHover ? styles.downloadButtonHover : {}),
              }}
              onClick={handleDownload}
              onMouseEnter={() => setDownloadHover(true)}
              onMouseLeave={() => setDownloadHover(false)}
            >
              Download File
            </button>
          </div>
        );
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes modalSlideIn {
            from {
              opacity: 0;
              transform: scale(0.95);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
          
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
      <div style={styles.overlay} onClick={onClose}>
        <div style={styles.container} onClick={(e) => e.stopPropagation()}>
          <div style={styles.header}>
            <div style={styles.headerLeft}>
              <p style={styles.fileName}>{fileName}</p>
              <span style={styles.fileType}>{fileType}</span>
            </div>
            <div style={styles.headerRight}>
              <button
                style={{
                  ...styles.downloadBtn,
                  ...(downloadHover ? { backgroundColor: "#d97706" } : {}),
                }}
                onClick={handleDownload}
                onMouseEnter={() => setDownloadHover(true)}
                onMouseLeave={() => setDownloadHover(false)}
              >
                <span>⬇️</span> Download
              </button>
              <button
                style={{
                  ...styles.closeButton,
                  ...(closeHover ? { backgroundColor: "#e5e7eb", color: "#111827" } : {}),
                }}
                onClick={onClose}
                onMouseEnter={() => setCloseHover(true)}
                onMouseLeave={() => setCloseHover(false)}
              >
                ✕
              </button>
            </div>
          </div>

          <div style={styles.content}>{renderPreview()}</div>
        </div>
      </div>
    </>
  );
};

export default FilePreviewModal;

const styles = {
  overlay: {
    position: "fixed" as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.85)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    padding: "20px",
  },
  container: {
    background: "white",
    borderRadius: "12px",
    width: "90%",
    height: "90%",
    maxWidth: "1400px",
    maxHeight: "900px",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    display: "flex",
    flexDirection: "column" as const,
    overflow: "hidden",
    animation: "modalSlideIn 0.3s ease-out",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 24px",
    borderBottom: "1px solid #e5e7eb",
    backgroundColor: "#f9fafb",
    flexShrink: 0,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    flex: 1,
  },
  fileName: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#111827",
    margin: 0,
    maxWidth: "500px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },
  fileType: {
    fontSize: "12px",
    color: "#6b7280",
    backgroundColor: "#e5e7eb",
    padding: "4px 12px",
    borderRadius: "12px",
    fontWeight: 500,
    textTransform: "uppercase" as const,
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  downloadBtn: {
    background: "#f59e0b",
    color: "#111827",
    border: "none",
    padding: "8px 16px",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.2s",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  closeButton: {
    background: "none",
    border: "none",
    fontSize: "24px",
    color: "#6b7280",
    cursor: "pointer",
    padding: 0,
    width: "36px",
    height: "36px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "6px",
    transition: "all 0.2s",
  },
  content: {
    flex: 1,
    overflow: "auto",
    backgroundColor: "#f3f4f6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
  },
  imageContainer: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  image: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain" as const,
    borderRadius: "8px",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  },
  pdfContainer: {
    width: "100%",
    height: "100%",
  },
  csvContainer: {
    width: "100%",
    height: "100%",
    backgroundColor: "white",
    borderRadius: "8px",
    overflow: "hidden",
  },
  tableWrapper: {
    width: "100%",
    height: "100%",
    overflow: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse" as const,
    fontSize: "14px",
  },
  headerRow: {
    backgroundColor: "#f9fafb",
    position: "sticky" as const,
    top: 0,
    zIndex: 10,
  },
  headerCell: {
    padding: "12px 16px",
    textAlign: "left" as const,
    fontWeight: 600,
    color: "#111827",
    borderBottom: "2px solid #e5e7eb",
    borderRight: "1px solid #e5e7eb",
    whiteSpace: "nowrap" as const,
    backgroundColor: "#f9fafb",
  },
  dataRow: {
    backgroundColor: "white",
  },
  dataCell: {
    padding: "12px 16px",
    borderBottom: "1px solid #e5e7eb",
    borderRight: "1px solid #e5e7eb",
    color: "#374151",
  },
  excelContainer: {
    width: "100%",
    height: "100%",
    backgroundColor: "white",
    borderRadius: "8px",
    overflow: "hidden",
  },
  spreadsheet: {
    width: "100%",
    height: "100%",
  },
  iframe: {
    width: "100%",
    height: "100%",
    border: "none",
    borderRadius: "8px",
  },
  videoContainer: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  video: {
    maxWidth: "100%",
    maxHeight: "100%",
    borderRadius: "8px",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  },
  audioContainer: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  audio: {
    width: "80%",
    maxWidth: "600px",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    gap: "16px",
  },
  spinner: {
    width: "48px",
    height: "48px",
    border: "4px solid #e5e7eb",
    borderTop: "4px solid #f59e0b",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  loadingText: {
    fontSize: "16px",
    color: "#6b7280",
    margin: 0,
  },
  unsupportedContainer: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    gap: "16px",
    padding: "40px",
    textAlign: "center" as const,
  },
  unsupportedIcon: {
    fontSize: "64px",
    opacity: 0.5,
  },
  wordIcon: {
    fontSize: "64px",
  },
  unsupportedTitle: {
    fontSize: "20px",
    fontWeight: 600,
    color: "#111827",
    margin: 0,
  },
  unsupportedText: {
    fontSize: "15px",
    color: "#6b7280",
    margin: 0,
    maxWidth: "500px",
    lineHeight: "1.6",
  },
  errorContainer: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    gap: "16px",
    padding: "40px",
    textAlign: "center" as const,
  },
  errorIcon: {
    fontSize: "64px",
  },
  errorTitle: {
    fontSize: "20px",
    fontWeight: 600,
    color: "#ef4444",
    margin: 0,
  },
  errorText: {
    fontSize: "15px",
    color: "#6b7280",
    margin: 0,
    maxWidth: "500px",
    lineHeight: "1.6",
  },
  buttonGroup: {
    display: "flex",
    gap: "12px",
    marginTop: "8px",
    flexWrap: "wrap" as const,
    justifyContent: "center",
  },
  downloadButton: {
    backgroundColor: "#f59e0b",
    color: "#111827",
    border: "none",
    padding: "12px 32px",
    borderRadius: "8px",
    fontSize: "15px",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.2s",
  },
  downloadButtonHover: {
    backgroundColor: "#d97706",
  },
  secondaryButton: {
    backgroundColor: "#e5e7eb",
    color: "#111827",
    border: "none",
    padding: "12px 32px",
    borderRadius: "8px",
    fontSize: "15px",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.2s",
  },
};
